import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { Card, Col, Form, Row } from 'react-bootstrap';
import Select from 'react-select';
import Pagination from '@material-ui/lab/Pagination';
import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { Button as ChakraButton, IconButton } from '@chakra-ui/react';
import { FaHistory, FaPlusCircle, FaSearch, FaToggleOn, FaToggleOff, FaTimesCircle } from 'react-icons/fa';

import { ApplicationState } from '../../../store';
import { CompanyMaster } from '../../../models/CompanyMaster';
import { useAdminContext } from '../../../context/AdminProvider';
import { logAdminAction } from '../../../helpers/adminLog';
import AdminActionLogModal from '../../log/AdminActionLogModal';

interface FolderGroup {
    group_id: string;
    group_name: string;
    sid: string;
}

interface FolderGroupUser {
    group_id: string;
    username: string;
    sid: string;
    is_active: boolean;
    group_name: string;
    created_on: string;
}

interface GroupResponse {
    total_count: number;
    result_list: FolderGroup[];
}

interface GroupUserResponse {
    total_count: number;
    result_list: FolderGroupUser[];
}

interface SelectOption {
    value: string;
    label: string;
}

const EMPTY_FORM: FolderGroupUser = {
    group_id: '', username: '', sid: '', is_active: true, group_name: '', created_on: ''
};

const selectMenuStyle = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

function FolderGroupUserMapping() {
    const [allGroups, setAllGroups] = useState<FolderGroup[]>([]);
    const [records, setRecords] = useState<FolderGroupUser[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form, setForm] = useState<FolderGroupUser>(EMPTY_FORM);

    const [searchGroupId, setSearchGroupId] = useState('');
    const [searchUsername, setSearchUsername] = useState('');
    const [searchSid, setSearchSid] = useState('');
    const [activeFilter, setActiveFilter] = useState({ group_id: '', username: '', sid: '' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [isLogOpen, setIsLogOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [searchUsers, setSearchUsers] = useState<SelectOption[]>([]);
    const [isLoadingSearchUsers, setIsLoadingSearchUsers] = useState(false);
    const [modalUsers, setModalUsers] = useState<SelectOption[]>([]);
    const [isLoadingModalUsers, setIsLoadingModalUsers] = useState(false);

    const companies: CompanyMaster[] = useSelector<ApplicationState, CompanyMaster[]>(
        state => state.companyMaster?.responseCompanyMaster?.result_list ?? []
    );
    const { userRole } = useAdminContext();

    const companyOptions: SelectOption[] = useMemo(() => {
        if (companies.length === 0 || userRole.length === 0) return [];
        const hasMasterSid = userRole.some(r => r.sid === '*');
        if (hasMasterSid) return companies.map(c => ({ value: c.sid, label: c.company_name }));
        const allowedSids = new Set(userRole.map(r => r.sid));
        return companies.filter(c => allowedSids.has(c.sid)).map(c => ({ value: c.sid, label: c.company_name }));
    }, [companies, userRole]);

    const isCompanyLocked = companyOptions.length === 1;

    const groupOptions: SelectOption[] = useMemo(() => {
        const filtered = searchSid
            ? allGroups.filter(g => g.sid === searchSid)
            : allGroups;
        return filtered.map(g => ({ value: g.group_id, label: g.group_name }));
    }, [allGroups, searchSid]);

    const findCompanyName = useCallback((sid: string) => {
        const found = companies.find(c => c.sid === sid);
        return found ? found.company_name : sid;
    }, [companies]);

    const token = () => localStorage.getItem('DMS_APPLICATION') ?? '';

    const fetchUsers = useCallback(async (
        sid: string,
        setUsers: React.Dispatch<React.SetStateAction<SelectOption[]>>,
        setLoading: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (!sid) { setUsers([]); return; }
        setLoading(true);
        try {
            const res = await fetch(`/v1/user?sid=${sid}&id=&role_access=&page=1&page_size=10000`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (!res.ok) throw new Error();
            const data = await res.json();
            setUsers((data.result_list ?? []).map((emp: { id: string }) => ({ value: emp.id, label: emp.id })));
        } catch {
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchGroups = useCallback(async (sid = '') => {
        try {
            const res = await fetch(`/v1/folder-group?sid=${sid}`, {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) throw await res.json();
            const data: GroupResponse = await res.json();
            setAllGroups(data.result_list ?? []);
        } catch {
            // silently fail
        }
    }, []);

    const fetchRecords = useCallback(async (group_id = '', username = '', sid = '') => {
        try {
            const params = group_id
                ? `group_id=${encodeURIComponent(group_id)}`
                : `username=${encodeURIComponent(username)}&sid=${sid}`;
            const res = await fetch(`/v1/folder-group-user?${params}`, {
                headers: { Authorization: `Bearer ${token()}` },
            });
            if (!res.ok) throw await res.json();
            const data: GroupUserResponse = await res.json();
            setRecords(data.result_list ?? []);
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'โหลดข้อมูลไม่สำเร็จ', 'error');
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    useEffect(() => {
        if (companyOptions.length === 1) {
            const sid = companyOptions[0].value;
            setSearchSid(sid);
            fetchGroups(sid);
            fetchUsers(sid, setSearchUsers, setIsLoadingSearchUsers);
        }
    }, [companyOptions]);

    const filteredRecords = useMemo(() => {
        return records.filter(r => {
            if (activeFilter.group_id && r.group_id !== activeFilter.group_id) return false;
            if (activeFilter.username && !r.username.toLowerCase().includes(activeFilter.username.toLowerCase())) return false;
            if (activeFilter.sid && r.sid !== activeFilter.sid) return false;
            return true;
        });
    }, [records, activeFilter]);

    const totalPages = Math.ceil(filteredRecords.length / pageSize);
    const pagedRecords = filteredRecords.slice((page - 1) * pageSize, page * pageSize);

    const handleSearch = async () => {
        setActiveFilter({ group_id: searchGroupId, username: searchUsername, sid: searchSid });
        setPage(1);
        await fetchRecords(searchGroupId, searchUsername, searchSid);
        setHasSearched(true);
    };

    const openCreate = () => {
        const sid = isCompanyLocked ? companyOptions[0].value : searchSid;
        setForm({ ...EMPTY_FORM, sid, group_id: searchGroupId });
        setIsModalOpen(true);
        if (sid) fetchUsers(sid, setModalUsers, setIsLoadingModalUsers);
        else setModalUsers([]);
    };

    const handleConfirm = async () => {
        if (!form.group_id || !form.username.trim() || !form.sid) {
            Swal.fire('กรุณากรอกข้อมูลให้ครบ', '', 'warning');
            return;
        }
        try {
            const res = await fetch('/v1/folder-group-user', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw await res.json();
            const data: GroupUserResponse = await res.json();
            setRecords(prev => {
                const newRecords = data.result_list ?? [];
                const otherGroup = prev.filter(r => r.group_id !== form.group_id);
                return [...otherGroup, ...newRecords];
            });
            setIsModalOpen(false);
            logAdminAction('ADM-DMS103', 'ADD_USER', form.username, form.sid, `group_id=${form.group_id}`);
            Swal.fire({ title: 'Success', icon: 'success', timer: 1500, showConfirmButton: false });
        } catch (err) {
            const msg = (err as any).message ?? '';
            const isDuplicate = msg.includes('23505') || msg.toLowerCase().includes('duplicate');
            Swal.fire('Error', isDuplicate ? 'มีข้อมูลซ้ำอยู่ในระบบแล้ว กรุณาตรวจสอบ User และ กลุ่ม' : msg || 'บันทึกไม่สำเร็จ', 'error');
        }
    };

    const handleToggleActive = async (rec: FolderGroupUser) => {
        const updated = { ...rec, is_active: !rec.is_active };
        try {
            const res = await fetch('/v1/folder-group-user', {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(updated),
            });
            if (!res.ok) throw await res.json();
            const data: GroupUserResponse = await res.json();
            setRecords(prev => {
                const otherGroup = prev.filter(r => r.group_id !== rec.group_id);
                return [...otherGroup, ...(data.result_list ?? [])];
            });
            logAdminAction('ADM-DMS103', 'TOGGLE_ACTIVE', rec.username, rec.sid, `group_id=${rec.group_id}, is_active=${!rec.is_active}`);
        } catch (err) {
            Swal.fire('Error', (err as any).message ?? 'อัปเดตไม่สำเร็จ', 'error');
        }
    };

    const handleDelete = (rec: FolderGroupUser) => {
        Swal.fire({
            title: 'ยืนยันการลบ?',
            text: `${rec.username} ออกจากกลุ่ม ${rec.group_name}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'ลบ',
            cancelButtonText: 'ยกเลิก',
        }).then(async result => {
            if (!result.isConfirmed) return;
            try {
                const res = await fetch(
                    `/v1/folder-group-user?group_id=${encodeURIComponent(rec.group_id)}&username=${encodeURIComponent(rec.username)}&sid=${encodeURIComponent(rec.sid)}`,
                    { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } }
                );
                if (!res.ok) throw await res.json();
                const data: GroupUserResponse = await res.json();
                setRecords(prev => {
                    const otherGroup = prev.filter(r => r.group_id !== rec.group_id);
                    return [...otherGroup, ...(data.result_list ?? [])];
                });
                logAdminAction('ADM-DMS103', 'DELETE_USER', rec.username, rec.sid, `group_id=${rec.group_id}`);
                Swal.fire({ title: 'Deleted!', icon: 'success', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', (err as any).message ?? 'ลบไม่สำเร็จ', 'error');
            }
        });
    };

    return (
        <>
            {/* Title */}
            <div className="mt-2">
                <div className="trapezoid" style={{ width: 'auto', display: 'inline-block' }}>
                    <span className="metadata-font">ADM-DMS103 Folder Group Mapping User</span>
                </div>
                <div style={{ display: 'inline', float: 'right' }}>
                    <ChakraButton
                        colorScheme="purple"
                        size="sm"
                        leftIcon={<FaHistory />}
                        className="mx-1"
                        onClick={() => setIsLogOpen(true)}
                    >
                        Log
                    </ChakraButton>
                    <ChakraButton
                        colorScheme="green"
                        size="sm"
                        leftIcon={<FaPlusCircle />}
                        className="mx-1"
                        onClick={openCreate}
                    >
                        Add User to Group
                    </ChakraButton>
                </div>
            </div>

            <AdminActionLogModal
                isOpen={isLogOpen}
                onClose={() => setIsLogOpen(false)}
                sourceModule="ADM-DMS103"
                defaultSid={searchSid}
            />

            {/* Search + Table */}
            <div className="mat-box employee-font font-size-employee">
                <Row>
                    <Col>
                        <Row className="trapezoid trapezoid-purple" style={{ margin: 'initial' }}>
                            <span style={{ fontSize: '15px' }}>Search Menu</span>
                        </Row>
                        <Card.Body
                            className="mat-box shadow"
                            style={{ borderColor: '#e5e6e9 #dfe0e4 #d0d1d5', borderStyle: 'solid', borderWidth: '1px' }}
                        >
                            <Row>
                                <Col xs={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>บริษัท :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก บริษัท"
                                            options={companyOptions}
                                            value={companyOptions.find(c => c.value === searchSid) ?? null}
                                            onChange={opt => {
                                                const sid = opt?.value ?? '';
                                                setSearchSid(sid);
                                                setSearchGroupId('');
                                                setSearchUsername('');
                                                fetchGroups(sid);
                                                fetchUsers(sid, setSearchUsers, setIsLoadingSearchUsers);
                                            }}
                                            isClearable={!isCompanyLocked}
                                            isDisabled={isCompanyLocked}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>กลุ่ม :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก กลุ่ม"
                                            options={groupOptions}
                                            value={groupOptions.find(g => g.value === searchGroupId) ?? null}
                                            onChange={opt => setSearchGroupId(opt?.value ?? '')}
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>User :</Form.Label>
                                        <Select
                                            menuPortalTarget={document.body}
                                            styles={selectMenuStyle}
                                            placeholder="เลือก User"
                                            options={searchUsers}
                                            value={searchUsers.find(u => u.value === searchUsername) ?? null}
                                            onChange={opt => setSearchUsername(opt?.value ?? '')}
                                            isDisabled={!searchSid}
                                            isLoading={isLoadingSearchUsers}
                                            isClearable
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={2} className="d-flex align-items-end pb-3">
                                    <ChakraButton
                                        leftIcon={<FaSearch />}
                                        colorScheme="green"
                                        size="sm"
                                        onClick={handleSearch}
                                    >
                                        ค้นหา
                                    </ChakraButton>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Col>
                </Row>

                <br />

                <Row>
                    <Col xs={6}>
                        <div className="d-flex justify-content-start my-2 my-lg-0 align-items-center">
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>Show&nbsp;&nbsp;</label>
                            <select
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: 'auto' }}
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <label style={{ fontSize: '14px', marginBottom: 0 }}>&nbsp;&nbsp;entries</label>
                        </div>
                    </Col>
                    <Col xs={6}>
                        <div className="d-flex justify-content-end my-2 my-lg-0">
                            <Pagination
                                count={totalPages}
                                shape="rounded"
                                showFirstButton
                                showLastButton
                                page={page}
                                onChange={(_, value) => setPage(value)}
                            />
                        </div>
                    </Col>
                </Row>

                {/* Table */}
                <Row className="mt-2">
                    <Col>
                        <table className="table table-bordered align-middle employee-font font-size-employee">
                            <thead>
                                <tr className="text-center table-active thead-light">
                                    <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Action</th>
                                    <th>User</th>
                                    <th>กลุ่ม</th>
                                    <th>บริษัท</th>
                                    <th style={{ width: '80px' }}>Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!hasSearched ? (
                                    <tr><td colSpan={5} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>กรุณากด ค้นหา เพื่อแสดงข้อมูล</td></tr>
                                ) : pagedRecords.length === 0 ? (
                                    <tr><td colSpan={5} style={{ background: '#b7b7a4', color: 'black', fontSize: '10pt', textAlign: 'center' }}>No Data.</td></tr>
                                ) : (
                                    pagedRecords.map((rec, i) => (
                                        <tr key={i}>
                                            <td className="text-center">
                                                <div className="flex gap-1 justify-around">
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme={rec.is_active ? 'green' : 'gray'}
                                                        size="sm"
                                                        icon={rec.is_active ? <FaToggleOn /> : <FaToggleOff />}
                                                        aria-label={rec.is_active ? 'Deactivate' : 'Activate'}
                                                        onClick={() => handleToggleActive(rec)}
                                                    />
                                                    <IconButton
                                                        variant="outline"
                                                        colorScheme="red"
                                                        size="sm"
                                                        icon={<FaTimesCircle />}
                                                        aria-label="Delete"
                                                        onClick={() => handleDelete(rec)}
                                                    />
                                                </div>
                                            </td>
                                            <td>{rec.username}</td>
                                            <td>{rec.group_name}</td>
                                            <td>{findCompanyName(rec.sid)}</td>
                                            <td className="text-center">
                                                <span className={`badge ${rec.is_active ? 'badge-success' : 'badge-secondary'}`}>
                                                    {rec.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </Col>
                </Row>
            </div>

            {/* Add User to Group Modal */}
            <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(false)}>
                <ModalHeader toggle={() => setIsModalOpen(false)}>Add User to Group</ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>บริษัท <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก บริษัท"
                            options={companyOptions}
                            value={companyOptions.find(c => c.value === form.sid) ?? null}
                            onChange={opt => {
                                const sid = opt?.value ?? '';
                                setForm(prev => ({ ...prev, sid, group_id: '', username: '' }));
                                fetchUsers(sid, setModalUsers, setIsLoadingModalUsers);
                            }}
                            isDisabled={isCompanyLocked}
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>กลุ่ม <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก กลุ่ม"
                            options={allGroups.filter(g => !form.sid || g.sid === form.sid).map(g => ({ value: g.group_id, label: g.group_name }))}
                            value={allGroups.filter(g => g.group_id === form.group_id).map(g => ({ value: g.group_id, label: g.group_name }))[0] ?? null}
                            onChange={opt => setForm(prev => ({ ...prev, group_id: opt?.value ?? '' }))}
                            isDisabled={!form.sid}
                        />
                    </div>
                    <div className="form-group mt-2">
                        <label>User <span style={{ color: 'red' }}>*</span></label>
                        <Select
                            menuPortalTarget={document.body}
                            styles={selectMenuStyle}
                            placeholder="เลือก User"
                            options={modalUsers}
                            value={modalUsers.find(u => u.value === form.username) ?? null}
                            onChange={opt => setForm(prev => ({ ...prev, username: opt?.value ?? '' }))}
                            isDisabled={!form.sid}
                            isLoading={isLoadingModalUsers}
                            isClearable
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleConfirm}>Add</Button>
                    <ChakraButton colorScheme="red" onClick={() => setIsModalOpen(false)}>Cancel</ChakraButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default FolderGroupUserMapping;
